import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { motion } from 'framer-motion';
import { useSubscription } from '../contexts/SubscriptionContext';

const MotionCard = motion(Card);

const Analytics: React.FC = () => {
  const { isPremium } = useSubscription();

  const weeklyProgressData = [
    { week: 'Week 1', health: 8, skills: 6, mindset: 4, relationships: 2 },
    { week: 'Week 2', health: 10, skills: 8, mindset: 6, relationships: 3 },
    { week: 'Week 3', health: 12, skills: 10, mindset: 8, relationships: 4 },
    { week: 'Week 4', health: 9, skills: 12, mindset: 10, relationships: 5 },
  ];

  const categoryData = [
    { name: 'Health', value: 35, color: '#10b981' },
    { name: 'Skills', value: 40, color: '#4a90e2' },
    { name: 'Mindset', value: 15, color: '#8b5cf6' },
    { name: 'Relationships', value: 10, color: '#ef4444' },
  ];

  const xpProgressData = [
    { day: 'Mon', xp: 50 },
    { day: 'Tue', xp: 80 },
    { day: 'Wed', xp: 120 },
    { day: 'Thu', xp: 90 },
    { day: 'Fri', xp: 150 },
    { day: 'Sat', xp: 200 },
    { day: 'Sun', xp: 100 },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 'bold' }}>
          Analytics & Insights
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Track your progress and gain insights into your personal development journey
        </Typography>

        {!isPremium && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(255, 215, 0, 0.1)', borderRadius: 2 }}>
            <Typography variant="body2">
              📊 Upgrade to Premium for advanced analytics, detailed reports, and historical data!
            </Typography>
          </Box>
        )}
      </Box>

      <Grid container spacing={4}>
        {/* Weekly Progress Chart */}
        <Grid item xs={12} lg={8}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <CardContent>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                Weekly Time Allocation
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={weeklyProgressData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="health" stackId="1" stroke="#10b981" fill="#10b981" />
                  <Area type="monotone" dataKey="skills" stackId="1" stroke="#4a90e2" fill="#4a90e2" />
                  <Area type="monotone" dataKey="mindset" stackId="1" stroke="#8b5cf6" fill="#8b5cf6" />
                  <Area type="monotone" dataKey="relationships" stackId="1" stroke="#ef4444" fill="#ef4444" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </MotionCard>
        </Grid>

        {/* Category Distribution */}
        <Grid item xs={12} lg={4}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <CardContent>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                Time Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}%`}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </MotionCard>
        </Grid>

        {/* XP Progress */}
        <Grid item xs={12} lg={6}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <CardContent>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                Daily XP Progress
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={xpProgressData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="xp" fill="#ffd700" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </MotionCard>
        </Grid>

        {/* Key Metrics */}
        <Grid item xs={12} lg={6}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <CardContent>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                Key Metrics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      87%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Goal Completion Rate
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      28
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Current Streak (days)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                      156h
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Time Invested
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      12
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Achievements Unlocked
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </MotionCard>
        </Grid>

        {/* Insights */}
        <Grid item xs={12}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <CardContent>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                Insights & Recommendations
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, backgroundColor: 'rgba(16, 185, 129, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, color: 'success.main' }}>
                      🎯 Great Progress!
                    </Typography>
                    <Typography variant="body2">
                      You're consistently hitting your health and fitness goals. Keep up the excellent work!
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, backgroundColor: 'rgba(245, 158, 11, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, color: 'warning.main' }}>
                      ⚡ Opportunity
                    </Typography>
                    <Typography variant="body2">
                      Consider allocating more time to relationship building for a more balanced approach.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                      🚀 Next Level
                    </Typography>
                    <Typography variant="body2">
                      You're close to unlocking advanced leadership skills. Focus on communication this week!
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Analytics; 